import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import { decodeRoleFromToken } from "../utils/jwt";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  /** Latest `user` for async handlers (e.g. `/me` after login) without stale closures. */
  const userRef = useRef(user);
  /** Bumped when `token` changes so in-flight `/me` cannot clear a newer session. */
  const authSessionRef = useRef(0);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    try {
      localStorage.removeItem("adCreativeAuthToken");
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  }, []);

  const loadMe = useCallback(async () => {
    const session = authSessionRef.current;
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      if (session !== authSessionRef.current) return;
      setUser(data);
    } catch (err) {
      if (session !== authSessionRef.current) return;
      const status = err?.response?.status;
      const authRejected = status === 401 || status === 404;
      if (authRejected) {
        logout();
      } else if (!userRef.current) {
        // Cold start: cannot hydrate the session; clear invalid or unknown state.
        logout();
      }
      // If we already have `user` (e.g. just set by `login()`), keep the session on transient /me failures.
    } finally {
      // Always clear loading: a superseded in-flight `/me` used to skip this when `session` no longer
      // matched after StrictMode/token changes, leaving the app stuck on the spinner forever.
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    loadMe();
    return () => {
      authSessionRef.current += 1;
    };
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    if (!data?.token) {
      throw new Error("Login response missing token");
    }
    // Invalidate any in-flight `/me` from a previous token so a late 401 cannot clear this session.
    authSessionRef.current += 1;
    const session = authSessionRef.current;
    localStorage.setItem("token", data.token);
    try {
      localStorage.setItem("adCreativeAuthToken", data.token);
    } catch {
      /* ignore quota / private mode */
    }
    // Hydrate from `/me` before committing React token state so `loadMe` and the login response
    // cannot race (e.g. `/me` 401/404 clearing a session that login just created).
    let nextUser = data.user;
    try {
      const meRes = await api.get("/api/auth/me");
      if (session === authSessionRef.current && meRes?.data) {
        nextUser = meRes.data;
      }
    } catch {
      /* keep login payload; token is still valid for retry via effect `loadMe` */
    }
    setToken(data.token);
    setUser(nextUser);
    userRef.current = nextUser;
    setLoading(false);
    return nextUser;
  }, []);

  const roleFromToken = useMemo(() => decodeRoleFromToken(token), [token]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      /** Role claim from JWT (use `user.role` from `/me` when available). */
      roleFromToken,
      login,
      logout,
      refreshUser: loadMe,
    }),
    [user, token, loading, roleFromToken, login, logout, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
